import { FC, useMemo } from 'react';
import classNames from 'classnames';

import styles from './Table.module.scss';

export interface SupportedStyles {
  // TODO: Other caption positions do not work well with existing styles
  captionSide: 'top';
  tableLayout: 'auto' | 'fixed';
  textAlign?:
    | 'start'
    | 'end'
    | 'left'
    | 'right'
    | 'center'
    | 'justify'
    | 'justify-all'
    | 'match-parent';
  verticalAlign?:
    | 'baseline'
    | 'sub'
    | 'super'
    | 'text-top'
    | 'text-bottom'
    | 'middle'
    | 'top'
    | 'bottom';
}

interface Column extends Omit<SupportedStyles, 'captionSide' | 'tableLayout'> {
  fieldName: string;
  headerName?: string;
}

interface Row extends Omit<SupportedStyles, 'captionSide' | 'tableLayout'> {
  [row: string]: any;
  headerName?: string;
  id: string | number;
}

interface Props {
  caption?: string;
  captionSide?: SupportedStyles['captionSide'];
  columns?: Array<Column>;
  rows?: Array<Row>;
  shouldFillContainer?: boolean;
  shouldFilterRowCellsWithoutDefinedColumns?: boolean;
  stripedColumns?: boolean;
  stripedRows?: boolean;
  tableLayout?: SupportedStyles['tableLayout'];
}

/**
 * Read only table
 */
const Table: FC<Props> = ({
  caption,
  captionSide = 'top',
  columns = [],
  rows = [],
  shouldFillContainer = false,
  shouldFilterRowCellsWithoutDefinedColumns = false,
  stripedRows = false,
  stripedColumns = false,
  tableLayout = 'fixed',
}) => {
  const shouldAddEmptyHeaderSlot =
    Boolean(rows.find(({ headerName }) => headerName !== undefined)) && columns.length > 0;
  const columnHeaders = columns.filter(({ headerName }) => Boolean(headerName));
  const tableRows = useMemo(() => {
    if (!shouldFilterRowCellsWithoutDefinedColumns) {
      return rows;
    }

    const columnFieldSet = new Set();
    for (const { fieldName } of columns) {
      columnFieldSet.add(fieldName);
    }

    // want to filter to only k,v where k is in columnFieldSet
    return rows.map(({ id, headerName, verticalAlign, ...restOfEntries }) => ({
      ...Object.fromEntries(
        Object.entries(restOfEntries).filter(([key]) => columnFieldSet.has(key))
      ),
      headerName,
      id,
      verticalAlign,
    }));
  }, [columns, rows, shouldFilterRowCellsWithoutDefinedColumns]);
  return (
    <table
      className={classNames(styles.table, { [styles['fill-container']]: shouldFillContainer })}
      style={{ tableLayout }}
    >
      {caption && (
        <caption className={styles.caption} style={{ captionSide }}>
          {caption}
        </caption>
      )}
      {columns.length > 0 && (
        <thead>
          <tr>
            {shouldAddEmptyHeaderSlot && <th key="empty"></th>}
            {columnHeaders.map(({ headerName }) => (
              <th key={headerName} scope="col">
                {headerName}
              </th>
            ))}
          </tr>
        </thead>
      )}
      {tableRows.length > 0 && (
        <tbody>
          {tableRows.map(({ headerName, id, verticalAlign, ...rowValues }) => (
            <tr key={id} className={classNames({ [styles['row-striped']]: stripedRows })}>
              {headerName && <th scope="row">{headerName}</th>}
              {Object.entries(rowValues).map(([cellKey, cellValue]) => (
                <td
                  key={cellKey}
                  className={classNames({ [styles['column-striped']]: stripedColumns })}
                  style={{ verticalAlign }}
                >
                  {cellValue}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      )}
    </table>
  );
};

export type { Props as TableProps };
export default Table;
